import React from 'react';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Mail, Phone, MapPin, type LucideIcon } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import upiOfficialLogo from '../assets/payment/upi-official.png';
import { type SocialPlatform } from '../lib/socialLinks';

const CASHFREE_LOGO_URL = 'https://cashfreelogo.cashfree.com/cashfreepayments/logosvgs/Group_4355.svg';

const CardsMark = () => (
  <div className="relative h-10 w-14 overflow-hidden rounded-xl bg-gradient-to-br from-[#1d4ed8] via-[#2563eb] to-[#7c3aed] shadow-[0_10px_24px_rgba(37,99,235,0.28)]">
    <div className="absolute inset-x-3 top-2.5 h-1 rounded-full bg-white/70" />
    <div className="absolute left-3 top-5 h-1.5 w-3 rounded-full bg-white/55" />
    <div className="absolute bottom-2.5 right-3 flex items-center">
      <span className="h-3.5 w-3.5 rounded-full bg-[#EB001B]/95" />
      <span className="-ml-1.5 h-3.5 w-3.5 rounded-full bg-[#F79E1B]/95" />
    </div>
  </div>
);

const UpiMark = () => (
  <img src={upiOfficialLogo} alt="UPI" className="h-7 w-auto object-contain" />
);

const NetBankingMark = () => (
  <div className="flex h-10 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-[#0f766e] via-[#0891b2] to-[#2563eb] shadow-[0_10px_24px_rgba(8,145,178,0.24)]">
    <svg viewBox="0 0 48 48" className="h-6 w-6" aria-hidden="true">
      <path d="M8 18 24 10l16 8v2H8v-2Z" fill="#FEF3C7" />
      <path d="M12 22h4v12h-4V22Zm10 0h4v12h-4V22Zm10 0h4v12h-4V22Z" fill="#FFFFFF" />
      <path d="M8 36h32v3H8v-3Zm3-18h26" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </div>
);

const CashfreeMark = () => (
  <img src={CASHFREE_LOGO_URL} alt="Cashfree Payments" className="h-7 w-auto object-contain" />
);

const paymentHighlights = [
  { label: 'Cards', artwork: <CardsMark /> },
  { label: 'UPI', artwork: <UpiMark /> },
  { label: 'Net Banking', artwork: <NetBankingMark /> },
  { label: 'Cashfree Secure', artwork: <CashfreeMark /> },
];

export const Footer: React.FC = () => {
  const { settings } = useSettings();
  const footerLogo = settings?.footerLogo || settings?.logo;
  const socialIconMap: Record<SocialPlatform, LucideIcon> = {
    facebook: Facebook,
    instagram: Instagram,
    twitter: Twitter,
    youtube: Youtube,
    linkedin: Linkedin,
  };
  const activeSocialLinks = (Object.entries(settings?.socialLinks || {}) as [SocialPlatform, { url: string; enabled: boolean }][])
    .filter(([, item]) => item.enabled && item.url);

  return (
    <footer className="border-t border-primary/15 bg-text pb-8 pt-12 text-background">
      <div className="container mx-auto px-4 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-10">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {footerLogo ? (
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/5 p-2 shadow-primary">
                  <img
                    src={footerLogo}
                    alt={settings.storeName}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/5 font-semibold text-background shadow-primary">
                  SS
                </div>
              )}
              <div>
                <h3 className="text-2xl font-bold text-background">{settings?.storeName || 'Silver Strix'}</h3>
                {settings?.tagline && <p className="text-xs uppercase tracking-[0.18em] text-accent">{settings.tagline}</p>}
              </div>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-background/78">
              Thoughtful scents and essentials for every space.
            </p>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-background/78">
              <p className="font-semibold text-background">{settings?.legalBusinessName || settings?.storeName}</p>
              {settings?.gstNumber && <p className="mt-1">GSTIN: {settings.gstNumber}</p>}
            </div>
            {activeSocialLinks.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {activeSocialLinks.map(([platform, item]) => {
                  const Icon = socialIconMap[platform];
                  return (
                    <a
                      key={platform}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={platform}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-background/80 transition hover:border-accent/40 hover:text-accent"
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-4 text-lg font-semibold text-accent">Shop</h4>
            <ul className="space-y-2 text-sm text-background/78">
              <li><a href="/products" className="transition-colors hover:text-background">All Products</a></li>
              <li><a href="/bestsellers" className="transition-colors hover:text-background">Best Sellers</a></li>
              <li><a href="/categories" className="transition-colors hover:text-background">Categories</a></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-4 text-lg font-semibold text-accent">Support</h4>
            <ul className="space-y-2 text-sm text-background/78">
              <li><a href="/refund-policy" className="transition-colors hover:text-background">Refund Policy</a></li>
              <li><a href="/terms" className="transition-colors hover:text-background">Terms & Conditions</a></li>
              <li><a href="/privacy" className="transition-colors hover:text-background">Privacy Policy</a></li>
              <li><a href="/contact" className="transition-colors hover:text-background">Contact Us</a></li>
            </ul>
          </div>

          <div className="space-y-3 text-sm text-background/78">
            <h4 className="mb-4 text-lg font-semibold text-accent">Contact</h4>
            {settings?.storeEmail && (
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <Mail size={16} className="text-accent" />
                <a href={`mailto:${settings.storeEmail}`} className="break-all transition-colors hover:text-background">{settings.storeEmail}</a>
              </div>
            )}
            {settings?.storePhone && (
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <Phone size={16} className="text-accent" />
                <a href={`tel:${settings.storePhone.replace(/\s/g, '')}`} className="transition-colors hover:text-background">{settings.storePhone}</a>
              </div>
            )}
            {(settings?.storeAddress || settings?.city || settings?.state) && (
              <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <MapPin size={16} className="mt-0.5 text-accent" />
                <span>
                  {settings.storeAddress && `${settings.storeAddress}, `}
                  {settings.city && `${settings.city}, `}
                  {settings.state && `${settings.state}`}
                  {settings.pincode && ` - ${settings.pincode}`}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mb-10 rounded-[28px] border border-white/10 bg-white/5 p-5 shadow-primary">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent">Payments We Accept</p>
              <h4 className="mt-2 text-xl font-semibold text-background">Pay securely with trusted methods across India</h4>
              <p className="mt-2 text-sm leading-relaxed text-background/70">
                Online payments are processed through Cashfree with support for UPI, major debit and credit cards, and net banking.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {paymentHighlights.map(({ label, artwork }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/10 px-4 py-3 text-sm text-background/80"
                >
                  <span className="flex min-h-11 min-w-[72px] items-center justify-center rounded-2xl border border-white/12 bg-white/95 px-3 shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
                    {artwork}
                  </span>
                  <span className="font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-4 text-sm text-background/65 md:flex-row">
          <p>© {new Date().getFullYear()} {settings?.legalBusinessName || settings?.storeName || 'Silver Strix'}. All rights reserved.</p>
          <div className="flex gap-4">
            <a href="/privacy" className="transition-colors hover:text-background">Privacy</a>
            <a href="/terms" className="transition-colors hover:text-background">Terms</a>
            <a href="/refund-policy" className="transition-colors hover:text-background">Refunds</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
